// src/pages/DebtStudio/components/DealForm/index.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  useDebtStudio,
  validateDealForm,
  buildOrUpdateStructure,
} from "pages/DebtStudio/context/DebtStudioContext";

import DealInfo from "./DealInfo";
import DealTerms from "./DealTerms";
import CostOfIssuance from "./CostOfIssuance";
import Refunding from "./Refunding";
import TabMenu from "components/TabMenu";
import { applyRefundingDateDefaults } from "pages/DebtStudio/lib/refundingDateDefaults";
import { isCombinationPurpose, normalizeDealPurpose } from "pages/DebtStudio/lib/combination";

const BASE_TABS = [
  { key: "deal", label: "Issue Info" },
  { key: "terms", label: "Dates & Terms" },
  { key: "costs", label: "Costs & Sources" },
];

const REFUNDING_TAB = { key: "refunding", label: "Refunding" };

function norm(v) {
  return String(v ?? "").trim().toLowerCase();
}

function safeJson(value, fallback) {
  if (value == null) return fallback;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }
  return value;
}

function numOrNull(x) {
  if (x === "" || x == null) return null;
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

/**
 * Canonical -> UI rows
 * Canonical refunded_issues uses: issue_name / deposit_amount (and/or other variants)
 * UI expects: issueName / amount
 */
function uiRowsFromRefundedIssues(refundedIssues) {
  const arr = safeJson(refundedIssues, []);
  if (!Array.isArray(arr) || arr.length === 0) return null;

  return arr
    .filter((r) => r && typeof r === "object")
    .map((r) => {
      const issueName =
        r.issueName ??
        r.issue_name ??
        r.issue ??
        r.name ??
        r.label ??
        "";

      const amount =
        r.amount ??
        r.deposit_amount ??
        r.depositAmount ??
        r.deposit ??
        r.value ??
        r.deposit_required ??
        r.depositRequired ??
        r.deposit_requirement ??
        r.depositRequirement ??
        "";

      return {
        issueName: String(issueName ?? ""),
        amount: amount === "" ? "" : amount,
      };
    });
}

/**
 * UI rows -> Canonical refunded_issues
 * Canonical shape we want going forward:
 *   { entry_type: "manual", issue_name: string, deposit_amount: number|null }
 *
 * IMPORTANT:
 * Do NOT trim issue_name here. This function runs on every keystroke via onChange,
 * and trimming would remove trailing spaces, making it appear that the input
 * "won't accept spaces" when typing multi-word names.
 */
function refundedIssuesFromUiRows(rows) {
  const arr = Array.isArray(rows) ? rows : [];
  return arr.map((r) => ({
    entry_type: "manual",
    issue_name: String(r?.issueName ?? ""), // <- removed .trim()
    deposit_amount: numOrNull(r?.amount),
  }));
}

export default function DealForm() {
  const { form, setForm, structureRows, setStructureRows, isReadOnly } =
    useDebtStudio();

  const [activeTab, setActiveTab] = useState("deal");

  /* ------------------------------------------------------------------
   * Determine whether this is a refunding deal
   * IMPORTANT: This must reflect in-memory UI state, not DB.
   * ------------------------------------------------------------------ */
  const refundingTypeRaw = useMemo(() => {
    return (
      form?.type ??
      form?.dealType ??
      form?.deal?.type ??
      form?.deal_type ??
      ""
    );
  }, [form?.type, form?.dealType, form?.deal?.type, form?.deal_type]);

  const isRefundingDeal = useMemo(() => {
    return norm(refundingTypeRaw) === "refunding";
  }, [refundingTypeRaw]);

  const hasRefundingComponent = useMemo(() => {
    const purpose = norm(refundingTypeRaw);
    return purpose === "refunding" || normalizeDealPurpose(purpose) === "combined";
  }, [refundingTypeRaw]);

  /* ------------------------------------------------------------------
   * Determine whether this is a library deal
   * Source of truth per DebtStudioContext: form.dealClass === "library"
   * ------------------------------------------------------------------ */
  const dealClassRaw = useMemo(() => {
    return form?.dealClass ?? form?.deal_class ?? "";
  }, [form?.dealClass, form?.deal_class]);

  const isLibraryDeal = useMemo(() => {
    return norm(dealClassRaw) === "library";
  }, [dealClassRaw]);

  useEffect(() => {
    if (isReadOnly || !isRefundingDeal || isLibraryDeal) return;
    setForm((prev) => applyRefundingDateDefaults(prev, prev));
  }, [isReadOnly, isRefundingDeal, isLibraryDeal, form?.refunded_issues, setForm]);

  /* ------------------------------------------------------------------
   * Tabs
   * - Library deals hide Costs & Sources and Refunding
   * - Non-library refunding and combination deals include Refunding
   * ------------------------------------------------------------------ */
  const tabs = useMemo(() => {
    if (isLibraryDeal) {
      // Keep the flow minimal for library (read-only reference) deals
      return [
        { key: "deal", label: "Issue Info" },
        { key: "terms", label: "Dates & Terms" },
      ];
    }

    if (!hasRefundingComponent) return BASE_TABS;
    return [...BASE_TABS, REFUNDING_TAB];
  }, [isLibraryDeal, hasRefundingComponent]);

  /* ------------------------------------------------------------------
   * Guard: if a tab disappears, reset active tab
   * ------------------------------------------------------------------ */
  useEffect(() => {
    const allowed = new Set(tabs.map((t) => t.key));
    if (!allowed.has(activeTab)) {
      setActiveTab("deal");
    }
  }, [tabs, activeTab]);

  // 🧭 One-time focus on Issue Info when coming from "New"
  useEffect(() => {
    const requested = sessionStorage.getItem("dealFormSubTab");
    if (requested === "Deal Info") {
      setActiveTab("deal");
      sessionStorage.removeItem("dealFormSubTab");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  /* ------------------------------------------------------------------
   * Auto-structure initialization
   * ------------------------------------------------------------------ */
  useEffect(() => {
    if (!form || isReadOnly) return;

    const { ok } = validateDealForm(form);

    if (ok && (!structureRows || structureRows.length === 0)) {
      const skeleton = buildOrUpdateStructure(form);
      if (skeleton.length > 0) {
        setStructureRows(skeleton);
      }
    }
  }, [form, structureRows, setStructureRows, isReadOnly]);

  /**
   * Refunding rows shown in the UI:
   * Prefer canonical refunded_issues, fallback to legacy refundingManualDeposits.
   * Must be declared BEFORE any early return to satisfy Rules of Hooks.
   */
  const refundingRowsForUi = useMemo(() => {
    if (!form) return [];
    const canonicalIssues = isCombinationPurpose(refundingTypeRaw)
      ? safeJson(form?.refunded_issues, []).filter(
          (entry) => String(entry?.entry_type ?? entry?.entryType ?? "manual").toLowerCase() !== "ds_issue"
        )
      : form?.refunded_issues;
    const fromCanonical = uiRowsFromRefundedIssues(canonicalIssues);
    if (fromCanonical) return fromCanonical;
    return Array.isArray(form?.refundingManualDeposits)
      ? form.refundingManualDeposits
      : [];
  }, [form, form?.refunded_issues, form?.refundingManualDeposits, refundingTypeRaw]);

  if (!form) return null;

  return (
    <>
      <TabMenu tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === "deal" && <DealInfo isReadOnly={isReadOnly} />}

      {activeTab === "terms" && <DealTerms isReadOnly={isReadOnly} />}

      {activeTab === "costs" && !isLibraryDeal && (
        <CostOfIssuance
          isReadOnly={isReadOnly}
          costOfIssuance={form.costOfIssuance || []}
          additionalSources={form.additionalSources || []}
          onChangeCostOfIssuance={(newCosts) => {
            if (isReadOnly) return;
            setForm((prev) => ({
              ...prev,
              costOfIssuance: newCosts,
            }));
          }}
          onChangeAdditionalSources={(newSources) => {
            if (isReadOnly) return;
            setForm((prev) => ({
              ...prev,
              additionalSources: newSources,
            }));
          }}
        />
      )}

      {activeTab === "refunding" &&
        hasRefundingComponent &&
        !isLibraryDeal && (
          <Refunding
            isReadOnly={isReadOnly}
            refundingMode={isCombinationPurpose(refundingTypeRaw) ? "manual" : (form.refundingMode || "manual")}
            manualOnly={isCombinationPurpose(refundingTypeRaw)}
            refundingManualDeposits={refundingRowsForUi}
            currentDealId={form.id}
            issuerId={form.issuerId}
            issuerName={form.issuerNameRaw}
            settlementDate={form.deliveryDate}
            refundedIssues={form.refunded_issues || []}
            onChangeRefundingMode={(mode) => {
              if (isReadOnly) return;
              setForm((prev) => applyRefundingDateDefaults(prev, {
                ...prev,
                refundingMode: mode,
              }));
            }}
            onChangeRefundingManualDeposits={(rows) => {
              if (isReadOnly) return;

              const canonical = refundedIssuesFromUiRows(rows);

              setForm((prev) => ({
                ...prev,
                refundingManualDeposits: rows, // legacy/UI
                refunded_issues: canonical, // canonical for Controls/solver/reporting
              }));
            }}
            onChangeRefundedIssues={(entries) => {
              if (isReadOnly) return;
              setForm((prev) => applyRefundingDateDefaults(prev, { ...prev, refunded_issues: entries }));
            }}
          />
        )}
    </>
  );
}
